// Sign-in, sessions and account requests.
//
// The app used to gate itself on a password compiled into this bundle and
// talk to api.php with one shared secret. Both are gone: the server issues a
// per-session token at sign-in, every request carries it, and the server
// decides what the holder may do. A session only lapses after a long stretch
// with no requests at all (KAC_IDLE_SECONDS in api.php); the server enforces
// that, not the browser.
import { API_URL } from "./api";

const TOKEN_KEY = "kac-token";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export function authHeaders(extra = {}) {
  const token = getToken();
  return token ? { ...extra, Authorization: `Bearer ${token}` } : { ...extra };
}

// Anything that talks to an authenticated endpoint goes through here, so a
// dead session is noticed in exactly one place. Listeners registered with
// onSessionExpired are told, and the app drops back to the sign-in screen.
const expiryListeners = new Set();

export function onSessionExpired(fn) {
  expiryListeners.add(fn);
  return () => expiryListeners.delete(fn);
}

function announceExpiry() {
  setToken("");
  expiryListeners.forEach(fn => {
    try {
      fn();
    } catch {}
  });
}

export class SessionExpiredError extends Error {
  constructor() {
    super("Your session has expired. Please sign in again.");
    this.name = "SessionExpiredError";
  }
}

export async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: authHeaders({
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    }),
  });
  if (res.status === 401) {
    announceExpiry();
    throw new SessionExpiredError();
  }
  return res;
}

async function readError(res, fallback) {
  try {
    const body = await res.json();
    return body?.error || fallback;
  } catch {
    return fallback;
  }
}

// Returns { user } on success. Throws an Error whose message is safe to show
// — the server distinguishes "wrong password" from "waiting for activation"
// so a pending user isn't left guessing.
export async function login(email, password) {
  const res = await fetch(`${API_URL}?login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error(await readError(res, "Could not sign in. Try again."));
  const body = await res.json();
  setToken(body.token);
  return body.user;
}

export async function requestAccount({ name, email, password }) {
  const res = await fetch(`${API_URL}?signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, password }),
  });
  if (!res.ok) throw new Error(await readError(res, "Could not send your request. Try again."));
  return true;
}

// Who the current token belongs to, or null if there isn't a live session.
// Called on load, so a token that expired while the tab was closed sends the
// user to the sign-in screen rather than into a half-working app.
export async function fetchSession() {
  if (!getToken()) return null;
  try {
    const res = await apiFetch("?session");
    if (!res.ok) return null;
    const body = await res.json();
    return body.user || null;
  } catch {
    return null;
  }
}

export async function logout() {
  try {
    await apiFetch("?logout", { method: "POST" });
  } catch {
    // A dead session is already signed out as far as the server cares.
  }
  setToken("");
}

export async function changePassword(current, next) {
  const res = await apiFetch("?password", {
    method: "POST",
    body: JSON.stringify({ current, next }),
  });
  if (!res.ok) throw new Error(await readError(res, "Could not change your password."));
  return true;
}

export async function fetchAccounts() {
  const res = await apiFetch("?accounts");
  if (!res.ok) throw new Error(await readError(res, "Could not load accounts."));
  return res.json();
}

export async function actOnAccount(id, action) {
  const res = await apiFetch("?accounts", {
    method: "POST",
    body: JSON.stringify({ id, action }),
  });
  if (!res.ok) throw new Error(await readError(res, "Could not update that account."));
  return true;
}

// --- Approvals -----------------------------------------------------------
// Staff placements and group changes are proposed rather than saved; an admin
// decides. See src/utils/approvals.js for the shape of a request.

export async function fetchApprovals({ all = false } = {}) {
  const res = await apiFetch(`?approvals${all ? "&all=1" : ""}`);
  if (!res.ok) throw new Error(await readError(res, "Could not load the approval queue."));
  return res.json();
}

export async function requestApproval({ type, payload, summary }) {
  const res = await apiFetch("?approvals", {
    method: "POST",
    body: JSON.stringify({ type, payload, summary }),
  });
  if (!res.ok) throw new Error(await readError(res, "Could not send that for approval."));
  const body = await res.json();
  return body.request;
}

// action: approve | reject | cancel. Cancel is for withdrawing your own
// request; the other two are the admin's to make.
export async function decideApproval(id, action, reason = "") {
  const res = await apiFetch("?approvals", {
    method: "POST",
    body: JSON.stringify({ id, action, reason }),
  });
  if (!res.ok) throw new Error(await readError(res, "Could not update that request."));
  return true;
}

export async function fetchActivity(limit = 500) {
  const res = await apiFetch(`?activity&limit=${limit}`);
  if (!res.ok) throw new Error(await readError(res, "Could not load the activity log."));
  return res.json();
}

// Puts one activity entry back. Resolves to { data, revision } — the schedule
// as it now stands, for the caller to adopt so this tab isn't left holding a
// copy the server has moved on from.
//
// A refusal carries why: the server won't undo a change something has been
// written over, so the error names the fields that moved and who moved them
// rather than quietly clobbering them.
export async function revertActivity(id) {
  const res = await apiFetch("?revert", {
    method: "POST",
    body: JSON.stringify({ id }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(body?.error || "Could not undo that change.");
    err.conflicts = body?.conflicts || [];
    err.since = body?.since || [];
    throw err;
  }
  return { data: body?.data || null, revision: body?.revision || "" };
}

// For things the server can't observe by diffing the roster — a Gmail send
// happens entirely in the browser, so it has to say so.
export async function recordActivity(action, summary, target = "") {
  try {
    await apiFetch("?activity", {
      method: "POST",
      body: JSON.stringify({ action, summary, target }),
    });
  } catch {
    // Never let a logging failure break the thing being logged.
  }
}
